﻿import type { Nullable } from '@src/shared/types/common';
import { RoutePolyline } from '@legacy/scripts/modules/yandex-route-polyline';
import type { MultiRouteData, RouteInfoSource, RouteMetrics } from '@legacy/scripts/types/modules/yandex-map';

// ─────────────────────────────────────────────────────────────────────────────
// Построение, кэширование и отрисовка Яндекс-маршрутов.
//
// Вспомогательный сервис YandexMap. Готовый объект MultiRoute из ymaps.route
// привязан к конкретной карте и гибнет вместе с ней, поэтому кэш хранит не объект,
// а «снимок»: геометрию линии, точки и длину/время. По снимку маршрут рисуется
// заново без обращения к routing API.
// ─────────────────────────────────────────────────────────────────────────────

// Точка маршрута (плейсмарк), сохранённая для повторного отображения.
type CachedWayPoint = {
    coords: number[];
    preset?: string;
    iconContent?: unknown;
    balloonContent?: string;
    hintContent?: string;
};

// Путь маршрута с числовыми длиной/временем. Базовый тип @types/yandex-maps
// возвращает IGeoObject[], поэтому сужаем до реально существующих методов.
type RoutePathLike = ymaps.RoutePath & RouteMetrics;

// Снимок построенного маршрута — то, что хранит кэш.
export type RouteSnapshot = {
    // Геометрия: все пути склеены в одну сжатую строку (см. RoutePolyline).
    paths: string;
    // Длина/время по каждому пути — только числовые (метры/секунды). Текст всегда
    // строится форматтерами YandexMap, поэтому кэш и живой маршрут совпадают.
    pathInfos: { lengthMeters: number; timeSeconds: number }[];
    lengthMeters: number;
    timeSeconds: number;
    strokeColor: string;
    strokeWidth: number;
    wayPoints: CachedWayPoint[];
};

// Результат получения маршрута: снимок и «живой» объект маршрута. Объект есть только
// у вызова, который реально запустил построение; при отдаче из кэша он null.
type RouteSnapshotResult<TRoutes extends Nullable<ymaps.router.Route> = Nullable<ymaps.router.Route>> = {
    snapshot: RouteSnapshot;
    routes: TRoutes;
};

// DTO бэкенда (Common.DTO/YandexRoute).
type YandexRoutePathInfoDto = {
    lengthMeters: number;
    timeSeconds: number;
};

type YandexRouteDto = {
    // Ключ маршрута — координаты точек в порядке следования. Он же ключ кэша на бэке.
    pointsKey: string;
    paths: string;
    pathInfos?: YandexRoutePathInfoDto[];
};

// Вид маршрута по умолчанию — повторяет оформление ymaps.route (линия и точки).
const defaultRouteStrokeColor = '#a0f';
const defaultRouteStrokeWidth = 4;

// Preset плейсмарка точки маршрута с номером внутри.
const wayPointPreset = 'islands#blueCircleIcon';

/**
 * Сервис построения, кэширования и отрисовки Яндекс-маршрутов.
 *
 * Зачем нужен: объект MultiRoute, который возвращает ymaps.route, привязан к
 * конкретной карте и умирает вместе с ней, а routing-запросы Яндекса платные.
 * Поэтому сервис хранит не сам объект маршрута, а его «снимок» (RouteSnapshot):
 * сжатую геометрию линии, точки и длину/время. По снимку маршрут рисуется заново
 * на любой карте без повторного обращения к routing API.
 *
 * Ключ кэша — сами точки маршрута (getRouteKey), без привязки к id сущности:
 * одинаковые точки — это всегда один и тот же маршрут, поэтому он переиспользуется
 * между страницами и пользователями, а изменённые точки дают новый ключ (без протухания).
 *
 * Источники снимка (в порядке приоритета):
 * 1. Локальный in-memory кэш — общий для всех карт страницы.
 * 2. Бэкенд-кэш /YandexRoute/Get по ключу из точек.
 * 3. Построение через ymaps.route с последующим сохранением на бэкенд.
 */
export class YandexRouteCache {
    // In-memory кэш снимков по ключу точек. Общий для всех карт страницы: vehicleMap,
    // driverMap и попапы используют один кэш, поэтому бэкенд дёргается один раз. Он же
    // защищает от повторной оплаты routing-запроса при повторном открытии попапа.
    private static readonly routeSnapshotCache = new Map<string, RouteSnapshot>();

    // Строящиеся прямо сейчас маршруты: координаты → промис. Дедупликация «в полёте»:
    // vehicleMap и driverMap в блоке «Карта» строят один маршрут одновременно.
    private static readonly pendingRouteSnapshots = new Map<
        string,
        Promise<RouteSnapshotResult>
    >();

    // ── Статические утилиты (без состояния, доступны и вьюшкам) ──

    /**
     * Обёртка над ymaps.route — выполняет платный routing-запрос Яндекса.
     *
     * @param coords координаты точек маршрута в порядке следования
     * @param callback вызывается с готовым объектом маршрута при успехе
     * @param onError необязательный обработчик ошибки (иначе пишет в console.error)
     */
    static route(
        coords: number[][],
        callback: (routes: ymaps.router.Route) => void,
        onError?: (err: unknown) => void
    ): void {
        const result = window.ymaps.route(
            coords.map((point) => ({ type: 'wayPoint' as const, point })),
            {
                mapStateAutoApply: true,
                zoomMargin: 20
            }
        );

        result.done(
            (routes: ymaps.router.Route) => {
                callback(routes);
            },
            (err: unknown) => {
                if (onError) {
                    onError(err);
                }
                else {
                    console.error('YandexMap route error:', err);
                }
            }
        );
    }

    // ── Кэш и построение ──

    /**
     * Главная точка входа: возвращает снимок маршрута по координатам.
     *
     * Ключ кэша — сами точки (getRouteKey), поэтому одинаковые точки на любых
     * страницах (попап, гриды, заявки, отчёты) дают один маршрут, а изменённые
     * точки — новый ключ. Никакого id сущности не требуется.
     *
     * Настоящий объект маршрута (`routes`) получает только тот вызов, который реально
     * запустил построение; остальные получают `routes: null` и рисуют линию из снимка.
     */
    obtainSnapshot(
        coords: number[][]
    ): Promise<RouteSnapshotResult> {
        return this.obtainFromBackend(coords);
    }

    /**
     * Строит маршрут через Яндекс (минуя кэш) и возвращает снимок вместе с «живым»
     * объектом маршрута. Используется, когда кэша нет и маршрут нужно построить заново.
     */
    buildSnapshot(coords: number[][]): Promise<RouteSnapshotResult<ymaps.router.Route>> {
        return new Promise((resolve, reject) => {
            YandexRouteCache.route(
                coords,
                (routes) => {
                    resolve({ snapshot: this.extractRouteSnapshot(routes), routes });
                },
                (err) => reject(err)
            );
        });
    }

    /**
     * Сохраняет снимок маршрута на бэкенд (PUT /YandexRoute/Save) как fire-and-forget:
     * ошибка сохранения не критична — маршрут останется в локальном кэше, просто при
     * следующем открытии его снова придётся строить через Яндекс.
     */
    saveToBackend(coords: number[][], snapshot: RouteSnapshot): void {
        window.ajaxPut('/YandexRoute/Save', this.toBackendDto(coords, snapshot));
    }

    /**
     * Создаёт «заглушку» маршрута из снимка. Нужна, чтобы код попапа
     * (formatRouteInfo и т.п.) одинаково работал и с живым маршрутом, и с кэшем:
     * заглушка повторяет интерфейс getPaths/getTime/getLength (RouteInfoSource).
     */
    createRouteShim(cached: RouteSnapshot): RouteInfoSource {
        const paths = cached.pathInfos.map((info) => ({
            getLength: () => info.lengthMeters,
            getTime: () => info.timeSeconds
        }));

        return {
            getPaths: () => ({ toArray: () => paths }),
            getTime: () => cached.timeSeconds,
            getLength: () => cached.lengthMeters
        };
    }

    // ── Отрисовка из снимка ──

    /**
     * Подгоняет видимую область карты под все координаты маршрута. При построении
     * через ymaps.route это делает сам Яндекс (mapStateAutoApply), а при отрисовке
     * из кэша границы выставляем вручную, чтобы маршрут целиком попал в кадр.
     */
    setBoundsForCoords(map: ymaps.Map, allCoords: number[][]): void {
        const coords = allCoords.filter((c) => c.length === 2);

        if (!coords.length) {
            return;
        }

        let minLat = Number.POSITIVE_INFINITY;
        let maxLat = Number.NEGATIVE_INFINITY;
        let minLng = Number.POSITIVE_INFINITY;
        let maxLng = Number.NEGATIVE_INFINITY;

        coords.forEach(([lat, lng]) => {
            minLat = Math.min(minLat, lat);
            maxLat = Math.max(maxLat, lat);
            minLng = Math.min(minLng, lng);
            maxLng = Math.max(maxLng, lng);
        });

        void map.setBounds([[minLat, minLng], [maxLat, maxLng]], { checkZoomRange: true });
    }

    /**
     * Рисует одиночный маршрут из снимка (используется в setRoute): линия дефолтным
     * цветом + точки с номерами, затем подгоняет границы карты.
     */
    renderSnapshot(map: ymaps.Map, snapshot: RouteSnapshot): void {
        const allCoords = this.drawRouteLine(map, snapshot, snapshot.strokeColor, snapshot.strokeWidth);

        this.drawWayPoints(map, snapshot.wayPoints);
        this.setBoundsForCoords(map, allCoords);
    }

    /**
     * Рисует один маршрут из снимка внутри setMultiRoute: линия цветом маршрута и
     * (при showPoints) пронумерованные метки с адресами из routeData.points.
     * Координаты складываются в общий массив allCoords, чтобы подогнать границы
     * один раз после отрисовки всех маршрутов.
     */
    renderMultiRouteItem(map: ymaps.Map, snapshot: RouteSnapshot, routeData: MultiRouteData, allCoords: number[][]): void {
        allCoords.push(...this.drawRouteLine(map, snapshot, routeData.colorForRoute, 4));

        if (routeData.showPoints) {
            routeData.points.forEach((point, i) => {
                if (point.address) {
                    map.geoObjects.add(new window.ymaps.Placemark(point.coords, {
                        balloonContent: point.address,
                        hintContent: point.address,
                        iconContent: (i + 1).toString()
                    }));
                }
            });
        }
    }

    // ── Приватные ──

    /**
     * Получение снимка по ключу из точек: локальный кэш → дедупликация «в полёте» →
     * бэкенд-кэш / построение. Ключ одинаков для всех вызовов с теми же точками,
     * поэтому vehicleMap и driverMap в блоке «Карта» делают один запрос на двоих.
     */
    private obtainFromBackend(
        coords: number[][]
    ): Promise<RouteSnapshotResult> {
        const key = YandexRouteCache.getRouteKey(coords);

        const cached = YandexRouteCache.routeSnapshotCache.get(key);

        if (cached) {
            return Promise.resolve({ snapshot: cached, routes: null });
        }

        const pending = YandexRouteCache.pendingRouteSnapshots.get(key);

        if (pending) {
            return pending.then(({ snapshot }) => ({ snapshot, routes: null }));
        }

        const ownerPromise = this.fetchBackendOrBuild(coords);
        YandexRouteCache.pendingRouteSnapshots.set(key, ownerPromise);

        return ownerPromise.then(
            ({ snapshot, routes }) => {
                YandexRouteCache.routeSnapshotCache.set(key, snapshot);
                YandexRouteCache.pendingRouteSnapshots.delete(key);
                return { snapshot, routes };
            },
            (err: unknown) => {
                YandexRouteCache.pendingRouteSnapshots.delete(key);
                throw err;
            }
        );
    }

    /**
     * Сначала пробует бэкенд-кэш по ключу из точек, иначе строит маршрут через Яндекс
     * и сохраняет результат на бэкенд. Вызывается только владельцем промиса — остальные
     * получают результат из pending.
     */
    private async fetchBackendOrBuild(
        coords: number[][]
    ): Promise<RouteSnapshotResult> {
        const pointsKey = YandexRouteCache.getRouteKey(coords);
        const dto = await this.fetchRouteFromBackend(pointsKey);
        const cached = this.fromBackendDto(dto, coords);

        if (cached) {
            return { snapshot: cached, routes: null };
        }

        const { snapshot, routes } = await this.buildSnapshot(coords);
        this.saveToBackend(coords, snapshot);

        return { snapshot, routes };
    }

    // POST /YandexRoute/Get — читает сохранённую геометрию маршрута по ключу точек.
    // Именно POST, а не GET: ключ — это координаты всех точек, и на длинных маршрутах
    // (GPS-треки) он не влезает в query string. При ошибке или отсутствии маршрута
    // резолвится в null (не бросает исключение).
    private fetchRouteFromBackend(pointsKey: string): Promise<Nullable<YandexRouteDto>> {
        return new Promise((resolve) => {
            window.ajaxPost(
                '/YandexRoute/Get',
                { pointsKey },
                (response: unknown) => resolve(response as Nullable<YandexRouteDto>),
                () => resolve(null)
            );
        });
    }

    // Преобразует снимок в DTO бэкенда: ключ — координаты точек, плюс геометрия линии
    // и числовые длина/время по каждому пути.
    private toBackendDto(coords: number[][], snapshot: RouteSnapshot): YandexRouteDto {
        return {
            pointsKey: YandexRouteCache.getRouteKey(coords),
            paths: snapshot.paths,
            pathInfos: snapshot.pathInfos.map((p) => ({
                lengthMeters: Math.round(p.lengthMeters),
                timeSeconds: Math.round(p.timeSeconds)
            }))
        };
    }

    /**
     * Восстанавливает снимок из ответа бэкенда. Точки (wayPoints) берём из координат
     * запроса — бэкенд хранит только геометрию линии, а не плейсмарки.
     *
     * Возвращает null, если записи нет или она неполная (нет геометрии или длин/времён)
     * — это промах кэша, а не ошибка: вызвавший построит маршрут через Яндекс.
     */
    private fromBackendDto(dto: Nullable<YandexRouteDto>, coords: number[][]): Nullable<RouteSnapshot> {
        if (!dto?.paths || !dto.pathInfos?.length) {
            return null;
        }

        const pathInfos = dto.pathInfos.map((p) => ({
            lengthMeters: p.lengthMeters,
            timeSeconds: p.timeSeconds
        }));

        const lengthMeters = pathInfos.reduce((sum, p) => sum + p.lengthMeters, 0);
        const timeSeconds = pathInfos.reduce((sum, p) => sum + p.timeSeconds, 0);

        return {
            paths: dto.paths,
            pathInfos,
            lengthMeters,
            timeSeconds,
            strokeColor: defaultRouteStrokeColor,
            strokeWidth: defaultRouteStrokeWidth,
            wayPoints: coords.map((c, i) => ({
                coords: c,
                preset: wayPointPreset,
                iconContent: (i + 1).toString(),
                balloonContent: c.join(', '),
                hintContent: c.join(', ')
            }))
        };
    }

    /**
     * Формирует снимок из «живого» объекта маршрута: сжимает геометрию всех путей
     * через RoutePolyline.encodePaths, сохраняет длину/время по каждому пути и
     * извлекает точки (extractWayPoints) для последующего восстановления плейсмарков.
     */
    private extractRouteSnapshot(routes: ymaps.router.Route): RouteSnapshot {
        const paths = this.getPathArray(routes);

        return {
            paths: RoutePolyline.encodePaths(paths.map((path) => {
                const pathCoords: number[][] = [];
                path.getSegments().forEach((segment) => pathCoords.push(...segment.getCoordinates()));
                return pathCoords;
            })),
            pathInfos: paths.map((path) => ({
                lengthMeters: path.getLength(),
                timeSeconds: path.getTime()
            })),
            lengthMeters: routes.getLength(),
            timeSeconds: routes.getTime(),
            strokeColor: defaultRouteStrokeColor,
            strokeWidth: defaultRouteStrokeWidth,
            wayPoints: this.extractWayPoints(routes)
        };
    }

    // Извлекает точки маршрута, чтобы восстановить их как Placemark: номер, preset
    // и балун с координатами (как у живого маршрута Яндекса).
    private extractWayPoints(routes: ymaps.router.Route): CachedWayPoint[] {
        const result: CachedWayPoint[] = [];
        let index = 0;

        routes.getWayPoints().each((wp) => {
            const geo = wp as unknown as {
                geometry?: { getCoordinates?: () => number[] };
                options?: { get?: (key: string) => unknown };
                properties?: { get?: (key: string) => unknown };
            };

            const coords = geo.geometry?.getCoordinates?.() ?? [];

            result.push({
                coords,
                preset: geo.options?.get?.('preset') as string | undefined,
                iconContent: geo.properties?.get?.('iconContent') ?? (index + 1).toString(),
                balloonContent: (geo.properties?.get?.('balloonContent') as string | undefined) ?? coords.join(', '),
                hintContent: (geo.properties?.get?.('hintContent') as string | undefined) ?? coords.join(', ')
            });

            index += 1;
        });

        return result;
    }

    // Возвращает пути маршрута, сузив тип до реальных методов (getLength/getTime).
    private getPathArray(routes: ymaps.router.Route): RoutePathLike[] {
        return routes.getPaths().toArray() as RoutePathLike[];
    }

    // Ключ кэша — строка из координат точек в порядке следования.
    static getRouteKey(coords: number[][]): string {
        return coords.map((c) => `${c[0]},${c[1]}`).join('|');
    }

    /**
     * Декодирует сжатую геометрию снимка (RoutePolyline.decodePaths) и рисует линии
     * на карте. Возвращает все координаты, чтобы вызвавший код мог подогнать границы.
     */
    private drawRouteLine(map: ymaps.Map, snapshot: RouteSnapshot, strokeColor: string, strokeWidth: number): number[][] {
        const allCoords: number[][] = [];

        RoutePolyline.decodePaths(snapshot.paths).forEach((pathCoords) => {
            if (!pathCoords.length) {
                return;
            }

            map.geoObjects.add(new window.ymaps.Polyline(pathCoords, {}, { strokeColor, strokeWidth }));
            allCoords.push(...pathCoords);
        });

        return allCoords;
    }

    /**
     * Рисует точки маршрута (плейсмарки) из снимка: номер внутри иконки и балун
     * с координатами. Используется для одиночного маршрута (renderSnapshot).
     */
    private drawWayPoints(map: ymaps.Map, wayPoints: CachedWayPoint[]): void {
        wayPoints.forEach((wp) => {
            if (wp.coords.length === 0) {
                return;
            }

            const properties: Record<string, unknown> = {};

            if (wp.iconContent != null) {
                properties.iconContent = wp.iconContent;
            }

            if (wp.balloonContent != null) {
                properties.balloonContent = wp.balloonContent;
            }

            if (wp.hintContent != null) {
                properties.hintContent = wp.hintContent;
            }

            const options = wp.preset == null ? {} : { preset: wp.preset };
            map.geoObjects.add(new window.ymaps.Placemark(wp.coords, properties, options));
        });
    }
}
